/**
 * Business day utilities for Work Areas.
 * All dates computed in America/Santiago timezone.
 * next_followup_date is stored as DATE (no time) in PostgreSQL.
 */

// Get current date in Chile timezone (date parts only)
export function getTodayChile() {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(now).split('-')
  return new Date(+parts[0], +parts[1] - 1, +parts[2])
}

// Business days: skip weekends. Monday "AYER" = Friday
export function getBusinessYesterday(today) {
  const d = new Date(today)
  const day = d.getDay()
  const offset = day === 1 ? -3 : day === 0 ? -2 : -1
  d.setDate(d.getDate() + offset)
  return d
}

export function getBusinessTomorrow(today) {
  const d = new Date(today)
  const day = d.getDay()
  const offset = day === 5 ? 3 : day === 6 ? 2 : 1
  d.setDate(d.getDate() + offset)
  return d
}

export function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
}

// Parse a date string (YYYY-MM-DD or ISO) as date-only (no timezone shift)
export function parseDateOnly(dateStr) {
  if (!dateStr) return null
  const s = String(dateStr).slice(0, 10)
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// Classify a contact into a work area
export function classifyWorkArea(followupDate, today, yesterday, tomorrow) {
  if (!followupDate) return null
  const d = parseDateOnly(followupDate)
  if (!d) return null
  if (isSameDay(d, today)) return 'today'
  if (isSameDay(d, yesterday)) return 'yesterday'
  if (isSameDay(d, tomorrow)) return 'tomorrow'
  if (d < today) return 'overdue'
  return 'future'
}

// Count contacts per work area
export function countByWorkArea(contacts) {
  const today = getTodayChile()
  const yesterday = getBusinessYesterday(today)
  const tomorrow = getBusinessTomorrow(today)
  const result = { overdue: 0, yesterday: 0, today: 0, tomorrow: 0, future: 0 }
  contacts.forEach(c => {
    const area = classifyWorkArea(c.next_followup_date, today, yesterday, tomorrow)
    if (area) result[area]++
  })
  return result
}

// Filter contacts by work area
export function filterByWorkArea(contacts, area) {
  if (!area) return contacts
  const today = getTodayChile()
  const yesterday = getBusinessYesterday(today)
  const tomorrow = getBusinessTomorrow(today)
  return contacts.filter(c => classifyWorkArea(c.next_followup_date, today, yesterday, tomorrow) === area)
}

// Labels en espanol para cada area de trabajo
export const WORK_AREA_LABELS = {
  overdue: 'ATRASADOS',
  yesterday: 'AYER',
  today: 'HOY',
  tomorrow: 'MA\u00d1ANA',
  future: 'FUTUROS',
  all_mine: 'TODOS MIOS',
  activos: 'ACTIVOS',
  discarded: 'DESCARTADOS'
}

// Computa el area destino tras un cambio de next_followup_date.
// Retorna { area, label } o null si el cambio no mueve de area.
export function computeTargetArea(newFollowupDate, currentArea) {
  const today = getTodayChile()
  const yesterday = getBusinessYesterday(today)
  const tomorrow = getBusinessTomorrow(today)
  const area = classifyWorkArea(newFollowupDate, today, yesterday, tomorrow)
  if (!area) {
    // Sin fecha -> cae en ACTIVOS (si icono bulb_on) o TODOS MIOS; por defecto usamos TODOS MIOS
    return { area: 'all_mine', label: WORK_AREA_LABELS.all_mine }
  }
  // Si el area destino es la misma que la actual, no se considera cambio
  if (currentArea && area === currentArea) return null
  return { area, label: WORK_AREA_LABELS[area] || area.toUpperCase() }
}
